import React, { createContext, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { PageWrap } from '../../Objects/PageWrap/PageWrap';
import { ReactLink } from '../ReactLink/ReactLink';
import { Paragraph } from '../Paragraph/Paragraph';
import uuidv4 from '../../utils/uuidv4';
import { Icon } from '../Icon/Icon';
import { Heading } from '../Heading/Heading';
export const FooterIdContext = createContext(null);
export function useFooterId() {
    return useContext(FooterIdContext);
}
/**
 * Bronson Footer component.
 * @see https://bronson.vwfs.io/default/components/detail/bronson-footer--footer-full.html
 * @constructor
 */
export function Footer({ children, testId, ...otherProps }) {
    const footerId = useMemo(() => uuidv4(''), []);
    return (React.createElement(FooterIdContext.Provider, { value: footerId },
        React.createElement("footer", { className: "c-footer", "data-testid": testId, ...otherProps }, children)));
}
Footer.displayName = 'Footer';
/**
 * FooterModuleBreadcrumbs
 * @constructor
 */
export function FooterModuleBreadcrumbs({ children, testId, ...otherProps }) {
    return (React.createElement("div", { className: "c-footer__module c-footer__module--breadcrumb", "data-testid": testId, ...otherProps },
        React.createElement(PageWrap, null,
            React.createElement("div", { className: "c-footer-breadcrumb" }, children))));
}
FooterModuleBreadcrumbs.displayName = 'Footer.ModuleBreadcrumbs';
Footer.ModuleBreadcrumbs = FooterModuleBreadcrumbs;
/**
 * FooterModuleNavColumns
 * @constructor
 */
export function FooterModuleNavColumns({ children, navSectionLabel, testId, ...otherProps }) {
    const footerId = useFooterId();
    return (React.createElement("div", { className: "c-footer__module c-footer__module--nav", "data-testid": testId, ...otherProps },
        React.createElement(PageWrap, null,
            React.createElement("nav", { className: "c-footer-nav", "aria-labelledby": `footer-nav-${footerId}` },
                React.createElement("span", { id: `footer-nav-${footerId}`, className: "u-visually-hidden" }, navSectionLabel),
                React.createElement("div", { className: "c-footer-nav__columns" }, children)))));
}
FooterModuleNavColumns.displayName = 'Footer.ModuleNavColumns';
Footer.ModuleNavColumns = FooterModuleNavColumns;
/**
 * Bronson FooterNavColumn component (nested).
 * @constructor
 */
export function FooterNavColumn({ children, headingLevel = 4, title, testId }) {
    /**
     * Add state handling for footer accordion.
     */
    const [active, setActive] = useState(false);
    /**
     * Allocate unique identifiers for accessibility reasons.
     * @type {string|*}
     */
    const panelUUID = useMemo(() => uuidv4('footer-panel-'), []);
    const titleUUID = useMemo(() => uuidv4('footer-nav-title-'), []);
    const columnClassName = classNames('c-footer-nav__column', 'js-footer-accordion', // Used in Bronson CSS.
    'js-is-ready' // Used in Bronson CSS.
    );
    const titleClassName = classNames('c-footer-nav__title', {
        'is-active': active,
    });
    const panelClassName = classNames('c-footer-nav__list', 'js-is-ready', // Used in Bronson CSS.
    {
        'is-closed': !active,
    });
    return (React.createElement("div", { className: columnClassName, "data-testid": testId },
        React.createElement("div", { className: titleClassName, onClick: () => setActive(!active) },
            React.createElement(Heading, { id: titleUUID, className: "c-footer-nav__title-label is-heading", level: headingLevel }, title),
            React.createElement("button", { className: "c-footer-nav__title-label is-button", type: "button", "aria-expanded": active, "aria-controls": panelUUID }, title)),
        React.createElement("ul", { id: panelUUID, "aria-labelledby": titleUUID, className: panelClassName }, children)));
}
FooterNavColumn.displayName = 'Footer.NavColumn';
Footer.NavColumn = FooterNavColumn;
/**
 * Bronson FooterNavColumnItem component (nested).
 * @constructor
 */
export function FooterNavColumnItem({ children, href, linkProps, normalLink, onClick, testId, ...otherProps }) {
    return (React.createElement("li", { className: "c-footer-nav__item", "data-testid": testId, ...otherProps },
        React.createElement(ReactLink, { className: "c-footer-nav__link", onClick: onClick, href: href, normalLink: normalLink, ...linkProps }, children)));
}
FooterNavColumn.Item = FooterNavColumnItem;
Footer.NavColumn.Item = FooterNavColumn.Item;
FooterNavColumnItem.displayName = 'Footer.NavColumn.Item';
/**
 * FooterModuleMetaNav
 * @constructor
 */
export function FooterModuleMetaNav({ children, copyrightSign = '©', copyrightText, metaSectionLabel, socialItems, socialSectionLabel, withdrawButtonProps, testId, ...otherProps }) {
    const footerId = useFooterId();
    return (React.createElement("div", { className: "c-footer__module c-footer__module--meta-nav", "data-testid": testId, ...otherProps },
        React.createElement("div", { className: "o-page-wrap" },
            React.createElement("nav", { className: "c-footer-meta-bar", "aria-labelledby": `footer-meta-nav-${footerId}` },
                React.createElement("span", { id: `footer-meta-nav-${footerId}`, className: "u-visually-hidden" }, metaSectionLabel),
                React.createElement("ul", { className: "c-footer-meta-nav", "aria-labelledby": `footer-meta-nav-${footerId}` }, children),
                withdrawButtonProps && (React.createElement("span", { className: "c-footer-withdraw" }, withdrawButtonProps.isButton ? (React.createElement("button", { className: "c-btn c-btn--secondary", type: "button", onClick: withdrawButtonProps.onClick },
                    React.createElement("span", { className: "c-btn__text" }, withdrawButtonProps.label))) : (React.createElement(ReactLink, { className: "c-footer-nav__link", href: withdrawButtonProps.href, onClick: withdrawButtonProps.onClick, normalLink: withdrawButtonProps.normalLink, ...withdrawButtonProps.linkProps }, withdrawButtonProps.label)))),
                socialItems && (React.createElement(React.Fragment, null,
                    React.createElement("span", { id: `footer-social-media-${footerId}`, className: "u-visually-hidden" }, socialSectionLabel),
                    React.createElement("ul", { className: "c-footer-social-media", "aria-labelledby": `footer-social-media-${footerId}` }, socialItems.map(({ href, icon, label, ...linkAttrs }, i) => {
                        const iconClassNames = classNames('c-footer-social-media__icon');
                        return (React.createElement("li", { className: "c-footer-social-media__item", key: i },
                            React.createElement(ReactLink, { "aria-label": label, href: href, className: "c-footer-social-media__link", ...linkAttrs },
                                React.createElement(Icon, { className: iconClassNames, name: icon }))));
                    })))),
                React.createElement("span", { className: "c-footer-copyright" },
                    React.createElement("span", { className: "c-footer-copyright__sign" }, copyrightSign),
                    React.createElement("span", { className: "c-footer-copyright__text" }, copyrightText))))));
}
FooterModuleMetaNav.displayName = 'Footer.ModuleMetaNav';
Footer.ModuleMetaNav = FooterModuleMetaNav;
/**
 * Bronson FooterModuleMetaNavItem component (nested).
 * @constructor
 */
export function FooterModuleMetaNavItem({ children, href, linkProps, normalLink, onClick, testId, ...otherProps }) {
    return (React.createElement("li", { ...otherProps, className: "c-footer-meta-nav__item", "data-testid": testId },
        React.createElement(ReactLink, { className: "c-footer-meta-nav__link", onClick: onClick, href: href, normalLink: normalLink, ...linkProps }, children)));
}
FooterModuleMetaNavItem.displayName = 'Footer.ModuleMetaNav.Item';
FooterModuleMetaNav.Item = FooterModuleMetaNavItem;
Footer.ModuleMetaNav.Item = FooterModuleMetaNavItem;
/**
 * FooterModuleFootnotes
 * @constructor
 */
export function FooterModuleFootnotes({ children, footerText, testId, ...otherProps }) {
    function renderFooterText() {
        if (footerText) {
            /**
             * If {@link footerText} is a string, just return it as a single child.
             */
            if (typeof footerText === 'string') {
                return React.createElement(Paragraph, null, footerText);
            }
            return footerText;
        }
        return null;
    }
    return (React.createElement("div", { className: "c-footer__module c-footer__module--footnotes", "data-testid": testId, ...otherProps },
        React.createElement("div", { className: "o-page-wrap" },
            React.createElement("div", { className: "c-footer-footnotes" },
                renderFooterText(),
                children))));
}
FooterModuleFootnotes.displayName = 'Footer.ModuleFootnotes';
Footer.ModuleFootnotes = FooterModuleFootnotes;
